import { useState, useCallback, useRef } from 'react';
import { enhancedApi } from '../api';

/**
 * Debounced duplicate checks for client fields and invoice numbers.
 * results[field]: undefined — not checked; 'ok' — unique; string — duplicate error message.
 */
export const useDuplicateCheck = () => {
  const [checking, setChecking] = useState({});
  const [results, setResults] = useState({});
  const timeouts = useRef({});

  const clearField = useCallback((field) => {
    if (timeouts.current[field]) {
      clearTimeout(timeouts.current[field]);
      delete timeouts.current[field];
    }
    setChecking((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setResults((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    Object.keys(timeouts.current).forEach((k) => clearTimeout(timeouts.current[k]));
    timeouts.current = {};
    setChecking({});
    setResults({});
  }, []);

  const checkClientField = useCallback(
    (field, value, options = {}) => {
      const minLen = options.minLength ?? 3;
      const normalize = options.normalize ?? ((v) => String(v ?? '').trim());
      const processed = normalize(value);
      if (!processed || processed.length < minLen) {
        clearField(field);
        return;
      }
      if (timeouts.current[field]) clearTimeout(timeouts.current[field]);
      setChecking((prev) => ({ ...prev, [field]: true }));
      timeouts.current[field] = setTimeout(async () => {
        try {
          const res = await enhancedApi.checkDuplicate(field, processed);
          const exists = Boolean(res.data?.exists);
          setResults((prev) => ({
            ...prev,
            [field]: exists
              ? options.duplicateMessage || `This ${field} is already in use`
              : 'ok',
          }));
        } catch {
          setResults((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
        } finally {
          setChecking((prev) => ({ ...prev, [field]: false }));
          delete timeouts.current[field];
        }
      }, 500);
    },
    [clearField]
  );

  const checkInvoiceNumber = useCallback(
    (value, options = {}) => {
      const field = options.fieldKey || 'invoiceNumber';
      const minLen = options.minLength ?? 3;
      const v = String(value ?? '').trim();
      if (!v || v.length < minLen) {
        clearField(field);
        return;
      }
      if (timeouts.current[field]) clearTimeout(timeouts.current[field]);
      setChecking((prev) => ({ ...prev, [field]: true }));
      timeouts.current[field] = setTimeout(async () => {
        try {
          const res = await enhancedApi.checkInvoiceNumber(v);
          const exists = Boolean(res.data?.exists);
          setResults((prev) => ({
            ...prev,
            [field]: exists
              ? options.duplicateMessage || 'This invoice number is already in use'
              : 'ok',
          }));
        } catch {
          setResults((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
        } finally {
          setChecking((prev) => ({ ...prev, [field]: false }));
          delete timeouts.current[field];
        }
      }, 500);
    },
    [clearField]
  );

  return { checking, results, checkClientField, checkInvoiceNumber, clearField, resetAll };
};
